/**
 * Jobs tab inside the project view (§13).
 *
 * Two views in one tab:
 *   - List: table of pipeline jobs for this project, newest first.
 *   - Detail: click a row to see per-agent progress, cancel a running
 *     job, or re-run a completed job with a mode selector.
 *
 * Running jobs auto-refresh every 5s via pollJobStatus. The poll is
 * owned here (not in the store) so it's torn down cleanly on unmount
 * and doesn't fire when the user is on another tab.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import EmptyState from "../components/EmptyState.jsx";
import ErrorBubble from "../components/ErrorBubble.jsx";
import { AGENT_NAMES } from "../constants/agents.js";
import { useStore } from "../store/useStore.js";

// Mode labels mirror the agent names shown in the per-job agent list so
// users see the same vocabulary end-to-end. "Synthesis" on its own read as
// jargon in testing — "From Verification & Synthesis" matches the entry in
// AGENT_NAMES and makes the re-entry point obvious.
const MODE_LABELS = {
  full: "Full refresh",
  from_synthesis: "From Verification & Synthesis",
  from_account_plan: "From Account Plan",
  from_presentations: "From Value Pyramid",
};

const STATUS_CLASS = {
  pending: "job-status-pending",
  running: "job-status-running",
  completed: "job-status-completed",
  failed: "job-status-failed",
  cancelled: "job-status-cancelled",
};

const NON_TERMINAL = new Set(["pending", "running"]);

const dateFormat = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

function formatDuration(startIso, endIso) {
  if (!startIso) return "—";
  const start = new Date(startIso).getTime();
  const end = endIso ? new Date(endIso).getTime() : Date.now();
  const secs = Math.max(0, Math.round((end - start) / 1000));
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const rem = secs % 60;
  return `${mins}m ${rem}s`;
}

export default function ProjectJobsTab() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const jobs = useStore((s) => s.jobs);
  const jobsLoading = useStore((s) => s.jobsLoading);
  const jobsError = useStore((s) => s.jobsError);
  const fetchJobs = useStore((s) => s.fetchJobs);
  const createJob = useStore((s) => s.createJob);
  const cancelJob = useStore((s) => s.cancelJob);
  const deleteJob = useStore((s) => s.deleteJob);
  const pollJobStatus = useStore((s) => s.pollJobStatus);
  const pushToast = useStore((s) => s.pushToast);
  const currentProject = useStore((s) => s.currentProject);

  const [selectedJobId, setSelectedJobId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [starting, setStarting] = useState(false);
  const [rerunMode, setRerunMode] = useState("full");

  useEffect(() => {
    fetchJobs(projectId);
  }, [fetchJobs, projectId]);

  const selectedJob = useMemo(
    () => jobs.find((j) => j.jobId === selectedJobId) || null,
    [jobs, selectedJobId],
  );

  // Poll the selected job every 5s while it's still running.
  const pollRef = useRef(null);
  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (!selectedJobId || !selectedJob) return;
    if (!NON_TERMINAL.has(selectedJob.status)) return;

    pollRef.current = setInterval(() => {
      pollJobStatus(projectId, selectedJobId);
    }, 5000);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [selectedJobId, selectedJob, projectId, pollJobStatus]);

  const hasCompanyUrl = Boolean(currentProject?.companyUrl);
  const runningJob = jobs.find((j) => NON_TERMINAL.has(j.status));

  const handleStart = async (mode = "full", priorJobId = null) => {
    setActionError(null);
    setStarting(true);
    try {
      const response = await createJob(projectId, { mode, priorJobId });
      pushToast(
        response.warning
          ? `Pipeline started with warning: ${response.warning}`
          : "Pipeline started",
        response.warning ? "info" : "success",
      );
      setSelectedJobId(response.jobId);
    } catch (err) {
      setActionError(err.message);
    } finally {
      setStarting(false);
    }
  };

  const handleCancel = async (jobId) => {
    if (!confirm("Cancel this pipeline run? Partial outputs are kept.")) return;
    setActionError(null);
    try {
      await cancelJob(projectId, jobId);
      pushToast("Pipeline cancelled", "info");
    } catch (err) {
      setActionError(err.message);
    }
  };

  const handleDelete = async (jobId) => {
    if (
      !confirm(
        "Delete this pipeline run record? The generated files in the Documents tab are kept; only the run history entry is removed. This cannot be undone.",
      )
    )
      return;
    setActionError(null);
    try {
      await deleteJob(projectId, jobId);
      pushToast("Pipeline run deleted", "info");
      if (selectedJobId === jobId) setSelectedJobId(null);
    } catch (err) {
      setActionError(err.message);
    }
  };

  return (
    <div className="project-jobs">
      <div className="project-tab-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => handleStart("full")}
          disabled={starting || !hasCompanyUrl || Boolean(runningJob)}
          title={
            !hasCompanyUrl
              ? "Set a companyUrl on this project first"
              : runningJob
                ? "A pipeline is already running for this project"
                : undefined
          }
        >
          {starting ? "Starting…" : "Start research pipeline"}
        </button>
      </div>

      {!hasCompanyUrl && (
        <ErrorBubble
          message={
            "This project has no companyUrl. The research pipeline needs a prospect URL — edit the project to add one."
          }
        />
      )}

      {(jobsError || actionError) && (
        <ErrorBubble message={jobsError || actionError} />
      )}

      {jobsLoading && jobs.length === 0 ? (
        <div className="conversation-list-loading">Loading jobs…</div>
      ) : jobs.length === 0 ? (
        <EmptyState
          title="No pipeline runs yet"
          description="Kick off a research pipeline to generate industry, business, stakeholder, and account-plan briefs for this project."
        />
      ) : (
        <JobList
          jobs={jobs}
          projectId={projectId}
          selectedJobId={selectedJobId}
          onSelect={(id) => setSelectedJobId(id === selectedJobId ? null : id)}
          rerunMode={rerunMode}
          setRerunMode={setRerunMode}
          onCancel={handleCancel}
          onDelete={handleDelete}
          onRerun={(jobId, mode) => handleStart(mode, jobId)}
          onOpenOutput={(filename) =>
            navigate(
              `/projects/${projectId}/editor?filename=${encodeURIComponent(filename)}`,
            )
          }
          starting={starting}
          blockedByRunning={Boolean(runningJob)}
        />
      )}
    </div>
  );
}

function JobList({
  jobs,
  selectedJobId,
  onSelect,
  rerunMode,
  setRerunMode,
  onCancel,
  onDelete,
  onRerun,
  onOpenOutput,
  starting,
  blockedByRunning,
}) {
  return (
    <ul className="job-list">
      {jobs.map((job) => {
        const active = job.jobId === selectedJobId;
        return (
          <li
            key={job.jobId}
            className={`job-list-item ${active ? "active" : ""}`}
          >
            <button
              type="button"
              className="job-list-row"
              onClick={() => onSelect(job.jobId)}
              aria-expanded={active}
            >
              <span className={`job-status ${STATUS_CLASS[job.status] || ""}`}>
                {job.status}
              </span>
              <span className="job-mode">
                {MODE_LABELS[job.mode] || job.mode}
              </span>
              <span className="job-started">
                {job.createdAt
                  ? dateFormat.format(new Date(job.createdAt))
                  : "—"}
              </span>
              <span className="job-duration">
                {formatDuration(job.createdAt, job.completedAt)}
              </span>
            </button>
            {active && (
              <JobDetail
                job={job}
                rerunMode={rerunMode}
                setRerunMode={setRerunMode}
                onCancel={onCancel}
                onDelete={onDelete}
                onRerun={(mode) => onRerun(job.jobId, mode)}
                onOpenOutput={onOpenOutput}
                starting={starting}
                blockedByRunning={blockedByRunning}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

function JobDetail({
  job,
  rerunMode,
  setRerunMode,
  onCancel,
  onDelete,
  onRerun,
  onOpenOutput,
  starting,
  blockedByRunning,
}) {
  const agents = job.agents || {};
  const agentIds = Object.keys(agents)
    .map((k) => Number(k))
    .sort((a, b) => a - b);
  const running = NON_TERMINAL.has(job.status);
  const currentAgentId =
    typeof job.currentAgent === "number" ? job.currentAgent : null;
  const completedOutputs = agentIds
    .map((aid) => ({ aid, agent: agents[aid] || {} }))
    .filter(({ agent }) => agent.status === "completed" && agent.outputKey);

  return (
    <section className="job-detail">
      <header className="job-detail-header">
        <h3>
          {MODE_LABELS[job.mode] || job.mode} ·{" "}
          <span className={`job-status ${STATUS_CLASS[job.status] || ""}`}>
            {job.status}
          </span>
        </h3>
        {running && currentAgentId !== null && (
          <p className="job-detail-subtitle">
            Running: {AGENT_NAMES[currentAgentId] || `Agent ${currentAgentId}`}
          </p>
        )}
        {job.error && <ErrorBubble message={job.error.message || "Pipeline failed"} />}
      </header>

      <ol className="job-agent-list">
        {agentIds.map((aid) => {
          const a = agents[aid] || {};
          const isCurrent = aid === currentAgentId && running;
          return (
            <li
              key={aid}
              className={`job-agent-item job-agent-${a.status || "pending"} ${
                isCurrent ? "current" : ""
              }`}
            >
              <span className="job-agent-icon" aria-hidden="true">
                {a.status === "completed"
                  ? "✓"
                  : a.status === "failed"
                    ? "✕"
                    : a.status === "running"
                      ? "⏳"
                      : "·"}
              </span>
              <span className="job-agent-name">
                {AGENT_NAMES[aid] || `Agent ${aid}`}
              </span>
              <span className="job-agent-status">{a.status || "pending"}</span>
            </li>
          );
        })}
      </ol>

      {completedOutputs.length > 0 && (
        <div className="job-outputs">
          <h4>Output documents</h4>
          <ul className="job-outputs-list">
            {completedOutputs.map(({ aid, agent }) => (
              <li key={aid}>
                <button
                  type="button"
                  className="job-output-link"
                  onClick={() => onOpenOutput(agent.outputKey.replace(/^projects\/[^/]+\//, ""))}
                >
                  {AGENT_NAMES[aid] || `Agent ${aid}`}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <footer className="job-detail-actions">
        {running ? (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => onCancel(job.jobId)}
          >
            Cancel run
          </button>
        ) : (
          <>
            <label className="job-rerun-label">
              Re-run mode
              <select
                value={rerunMode}
                onChange={(e) => setRerunMode(e.target.value)}
              >
                <option value="full">Full refresh</option>
                <option value="from_synthesis">From Verification & Synthesis</option>
                <option value="from_account_plan">From Account Plan</option>
                <option value="from_presentations">From Value Pyramid</option>
              </select>
            </label>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => onRerun(rerunMode)}
              disabled={starting || blockedByRunning}
              title={
                blockedByRunning
                  ? "A pipeline is already running for this project"
                  : undefined
              }
            >
              {starting ? "Starting…" : "Re-run"}
            </button>
            <button
              type="button"
              className="btn btn-ghost job-delete-btn"
              onClick={() => onDelete(job.jobId)}
              title="Delete this run record"
              aria-label="Delete run"
            >
              Delete
            </button>
          </>
        )}
      </footer>
    </section>
  );
}
